<script setup lang="ts">
import type { JobEditProductItem } from '@/models/jobEdit';

import JobEditProductRow from '@/components/rows/JobEditProductRow.vue';
import { createEmptyProductItem } from '@/models/jobEdit';

export interface Props {
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
});

const model = defineModel<JobEditProductItem[]>({ default: () => [] });

function addProduct(): void {
  model.value = [...model.value, createEmptyProductItem()];
}

function removeProduct(index: number): void {
  model.value = model.value.filter((_, i) => i !== index);
}

function handleProductUpdate(index: number, value: JobEditProductItem): void {
  const updated = [...model.value];
  updated[index] = value;
  model.value = updated;
}

defineExpose({
  addProduct,
});
</script>

<template>
  <fieldset
    :class="$style.jobEditProductsFieldset"
    data-testid="jobEditProductsFieldset"
  >
    <div :class="$style.header">
      <div :class="$style.headerCell">Prefix</div>
      <div :class="$style.headerCell">Model</div>
      <div :class="$style.headerCell">Serial</div>
      <div :class="$style.headerCell">Description</div>
      <div :class="$style.headerCell">Quantity</div>
      <div :class="$style.headerCell">Quant ID</div>
      <div :class="$style.headerCell">Status</div>
      <div :class="$style.headerCellAction"></div>
    </div>

    <div :class="$style.body">
      <JobEditProductRow
        v-for="(product, index) in model"
        :key="index"
        :class="$style.row"
        :model-value="product"
        :readonly="props.readonly"
        @update:model-value="(value) => handleProductUpdate(index, value)"
        @delete="removeProduct(index)"
      />
    </div>
  </fieldset>
</template>

<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/grids';

.jobEditProductsFieldset {
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: none;
  padding: 0;
  margin: 0;
}

.header {
  display: grid;
  grid-template-columns: grids.$productsRowColumns;
  gap: 8px;
  padding: 8px 0 8px 8px;
  border-bottom: 2px solid colors.$color-gray-200;
}

.headerCell {
  font-size: 0.75rem;
  font-weight: 600;
  color: colors.$color-text-secondary;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.headerCellAction {
  width: 48px;
}

.body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.row {
  background-color: colors.$color-gray-50;
  border-radius: 4px;
}
</style>
